import { useState, useMemo } from 'react';
import { klantSignaalKleur, klantSignaalLabel } from '../../lib/inbox/klant-signaal-label';
import clsx from 'clsx';
import { useInboxStore } from '../../stores/inbox-store';
import { useTelephonyClient } from '../../hooks/useTelephonyClient';
import type { Brand } from '../../lib/telephony';
import { extractPhone, extractEmail } from '../../lib/inbox/bizziphone-parse';
import type { SuggestedMatch } from '../../stores/inbox-store';
import { supabase } from '../../lib/supabase';
import { OutboundComposerModal } from '../outbound/OutboundComposerModal';

/**
 * Vervangt ComposerPanel voor threads met `subtype='callback'`:
 * - BizziPhone-antwoordservice mails (438+ in productie)
 * - Phone-channel callbacks (toekomstige flow)
 *
 * MVP: klant-info + reden + suggesties + grote "Bel terug"-knop + dispo via status-buttons.
 * Date-picker reschedule + dispo-message-insert komt in latere iteratie.
 *
 * Geconsumeerd door ThreadDetail in batch D4.
 */

export function CallbackPanel() {
  const activeThread = useInboxStore((s) => s.activeThread);
  const isSending = useInboxStore((s) => s.isSending);
  const updateStatus = useInboxStore((s) => s.updateStatus);
  const selectNextThread = useInboxStore((s) => s.selectNextThread);
  // V3: het blok "Na het bellen" (notitie + In behandeling/Afhandelen) gebruikt
  // kantoor nooit (Willem 14-09); status zet je in de header.
  const lezerModus = useInboxStore((s) => s.lezerModus);
  const autoAdvanceEnabled = useInboxStore((s) => s.autoAdvanceEnabled);
  const linkCustomer = useInboxStore((s) => s.linkCustomerToConversation);
  const { dial } = useTelephonyClient();
  const [dispoNote, setDispoNote] = useState('');
  const [dispoSubmitting, setDispoSubmitting] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);

  const phoneToCall = useMemo(() => {
    if (!activeThread) return null;
    const conv = activeThread.conversation;
    if (conv.phone) return conv.phone;
    // Voor BizziPhone-mails: extract uit body van laatste inkomende message
    const lastIncoming = [...activeThread.messages].reverse().find(m => m.is_incoming);
    return extractPhone(lastIncoming?.body_text || '');
  }, [activeThread]);

  const reden = useMemo(() => {
    if (!activeThread) return '';
    const lastIncoming = [...activeThread.messages].reverse().find(m => m.is_incoming);
    const text = (lastIncoming?.body_text || '').trim();
    // Eerste 3 regels of 200 chars — geeft rep voldoende context zonder ruis
    const lines = text.split('\n').filter(l => l.trim()).slice(0, 3);
    const joined = lines.join(' ').trim();
    return joined.length > 200 ? joined.slice(0, 200) + '…' : joined;
  }, [activeThread]);

  if (!activeThread) return null;

  const conv = activeThread.conversation;
  const customerName = conv.customer_name || conv.from_name || 'Onbekend';
  const lastIncoming = [...activeThread.messages].reverse().find(m => m.is_incoming);
  const requestedAt = lastIncoming?.message_date || conv.last_message_at;

  const handleCall = async () => {
    if (!phoneToCall) return;
    setActionError(null);
    try {
      await dial(phoneToCall, conv.brand as Brand);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Bellen mislukt');
    }
  };

  // Notitie persisteren op de bron-call (calls.notes via PATCH callback).
  // BizziPhone-mailthreads hebben geen call-rij; dan is er niets om op te
  // persisteren en blijft de notitie alleen ter plekke (bewust).
  const persistDispoNote = async (): Promise<void> => {
    const sourceCallId = (conv as { callback_source_call_id?: string | null }).callback_source_call_id;
    if (!dispoNote.trim() || !sourceCallId) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Niet ingelogd');
    const r = await fetch('/api/telephony/callback', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ callId: sourceCallId, notes: dispoNote.trim() }),
    });
    if (!r.ok) {
      const body = (await r.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error || `Notitie opslaan mislukt (${r.status})`);
    }
  };

  const handleDispo = async (status: 'afgehandeld' | 'in_behandeling') => {
    setDispoSubmitting(true);
    setActionError(null);
    try {
      await persistDispoNote();
      await updateStatus(conv.id, status);
      setDispoNote('');
      if (autoAdvanceEnabled && status === 'afgehandeld') {
        void selectNextThread();
      }
    } catch (err) {
      // Notitie of statusflip mislukt: notitie NIET wissen, fout tonen.
      setActionError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setDispoSubmitting(false);
    }
  };

  const isAlreadyHandled = conv.status === 'afgehandeld';
  const ticketMatch = conv.subject?.match(/(\d{6,})/);
  const ticketNr = ticketMatch ? ticketMatch[1] : null;

  // "Stuur e-mail"-prefill: gekoppelde klant wint, anders best-effort uit de
  // BizziPhone-body (naam staat al in customerName, e-mail via extractEmail —
  // BizziPhone's eigen adressen tellen niet als klant-adres). Composer maakt
  // via /api/outbound/send een nieuw inbox-gesprek, ook zonder klantrecord.
  const linkedCustomer = activeThread.customer?.customer;
  const bodyEmail = extractEmail(lastIncoming?.body_text || '');
  const emailCustomer = linkedCustomer?.id
    ? { id: linkedCustomer.id, name: linkedCustomer.name, email: linkedCustomer.email, phone: linkedCustomer.phone }
    : {
        id: '',
        name: customerName !== 'Onbekend' ? customerName : '',
        email: bodyEmail && !bodyEmail.endsWith('@bizziphone.com') ? bodyEmail : '',
        phone: phoneToCall || '',
      };
  const emailAanleiding = `Terugbel-verzoek via BizziPhone antwoordservice. We mailen de klant in plaats van terug te bellen.\n\nGespreksverslag: ${reden}`;

  const customerLinked = activeThread.customer?.found === true;
  const suggestions: SuggestedMatch[] = !customerLinked
    ? (conv.suggested_customer_ids ?? []).slice(0, 5)
    : [];

  const handleAcceptSuggestion = async (s: SuggestedMatch) => {
    setLinkingId(s.id);
    try {
      await linkCustomer({
        conversationId: conv.id,
        customerId: s.id,
        customerType: s.type,
        customerName: s.name,
        contactId: s.contactId ?? undefined,
        contactName: s.contactName ?? undefined,
        matchSignal: 'suggestion_accepted',
      });
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <div className="border-t border-slate-200 bg-amber-50/40 px-6 py-5 flex-shrink-0">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
              <h3 className="text-base font-semibold text-slate-900">Terugbel-verzoek</h3>
              {ticketNr && <span className="text-xs text-slate-500">#{ticketNr}</span>}
            </div>
            <div className="text-sm text-slate-700">
              <span className="font-medium">{customerName}</span>
              {phoneToCall && <span className="text-slate-500"> · {phoneToCall}</span>}
            </div>
            {requestedAt && (
              <div className="text-xs text-slate-500 mt-0.5">
                Aangevraagd: {new Date(requestedAt).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>

        {/* Reden */}
        {reden && (
          <div className="mb-4 p-3 bg-white border border-slate-200 rounded-md">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Reden</div>
            <p className="text-sm text-slate-700 whitespace-pre-line">{reden}</p>
          </div>
        )}

        {/* Mogelijke matches uit auto-resolver — alleen tonen als nog geen klant gekoppeld */}
        {suggestions.length > 0 && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
              Mogelijke matches
            </div>
            <div className="space-y-1.5">
              {suggestions.map((s) => {
                const signalLabel = `${klantSignaalLabel(s.signal)} match`;
                const signalColor = klantSignaalKleur(s.signal);
                const detail = s.email ?? s.phone ?? '';
                return (
                  <button
                    key={s.id}
                    onClick={() => handleAcceptSuggestion(s)}
                    disabled={linkingId !== null}
                    className="w-full flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-md hover:border-indigo-400 hover:bg-indigo-50/30 disabled:opacity-50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-slate-900 truncate">{s.name}</span>
                        <span className={clsx('text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded', signalColor)}>
                          {signalLabel}
                        </span>
                        {s.type === 'lead' && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                            Lead
                          </span>
                        )}
                      </div>
                      {detail && (
                        <div className="text-xs text-slate-500 truncate">{detail}</div>
                      )}
                    </div>
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {actionError && (
          <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" role="alert">
            {actionError}
          </div>
        )}

        {/* Primary action */}
        {!isAlreadyHandled && (
          <button
            onClick={() => void handleCall()}
            disabled={!phoneToCall}
            className={clsx(
              'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors',
              phoneToCall
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed',
            )}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
            {phoneToCall ? `Bel terug naar ${phoneToCall}` : 'Geen telefoonnummer gevonden'}
          </button>
        )}

        {/* Secundair: mailen i.p.v. bellen — werkt ook zonder klantrecord */}
        {!isAlreadyHandled && (
          <button
            onClick={() => setShowEmailComposer(true)}
            className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            Stuur e-mail{emailCustomer.email ? ` naar ${emailCustomer.email}` : ''}
          </button>
        )}

        {/* Dispositie */}
        {!isAlreadyHandled && !lezerModus && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Na het bellen</div>
            <textarea
              value={dispoNote}
              onChange={(e) => setDispoNote(e.target.value)}
              placeholder="Optionele notitie (bv. 'voicemail ingesproken' of 'belt terug morgen')"
              rows={2}
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleDispo('in_behandeling')}
                disabled={dispoSubmitting || isSending}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50"
              >
                In behandeling
              </button>
              <button
                onClick={() => handleDispo('afgehandeld')}
                disabled={dispoSubmitting || isSending}
                className="flex-1 px-3 py-2 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Afhandelen
              </button>
            </div>
          </div>
        )}

        {isAlreadyHandled && (
          <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Afgehandeld
          </div>
        )}
      </div>

      <OutboundComposerModal
        open={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        customer={emailCustomer}
        // Merk volgt de order, niet het inkomende belkanaal: een Kleda-kleedorder
        // op een CINCO-callback moet als Kleda gemaild worden (Kleda-kennis incl.
        // doorlooptijd, juiste mailbox + tone). conv.brand is de fallback zonder order.
        orders={activeThread.customer?.orders}
        defaultBrand={conv.brand || undefined}
        prefillAanleiding={emailAanleiding}
      />
    </div>
  );
}
